<script lang="ts" setup>
import { DocumentAdd, Download, Close } from "@element-plus/icons-vue";
import { ElNotification, UploadProps, ElMessageBox } from "element-plus";
import type { UploadFile } from "element-plus";
import { onMounted } from "vue";
import { Attachment } from "~/types/attachment";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { FormSizeType } from "~/types/element-ui/FormSizeType";
import { useProductStore } from "~/stores/productStore";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { useDebouncedSearch } from "~/composable/useDebouncedSearch";
import { useLeadsStore } from "~/projects/crm/stores/leadsStore";
import { useOpportunitiesStore } from "~/projects/crm/stores/opportunitiesStore";
import { Opportunity, LeadSource, OpportunityType } from "~/projects/crm/types";
import { useOrganizationStore } from "~/projects/crm/stores/organizationStore";
import { OpportunityView } from "~/projects/crm/types/enums/opportunityView";

const leadsStore = useLeadsStore();
const opportunitiesStore = useOpportunitiesStore();
const productStore = useProductStore();
const profileStore = useCustomerProfileStore();
const organizationStore = useOrganizationStore();
const configStore = useConfigStore();

definePageMeta({
  layout: false,
});

const props = defineProps({
  showHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
});

onMounted(async () => {
  Object.assign(formData, getOpportunityFormData());

  if (formData.organization_id) {
    const simpleFilter = {
      organization_id: formData.organization_id,
    };

    profileStore.setSimpleFilter(simpleFilter);
  } else {
    profileStore.setSimpleFilter({});
  }
  await productStore.getAllProducts();
  await organizationStore.getSimpleOrganizations();
  await profileStore.getSimpleProfiles();
});

const fileList = ref<UploadFile[]>([]);

const showViewer = ref(false);
const previewIndex = ref(0);

const getStatusIdForOpportunityName = function (name) {
  return (
    configStore.globalConfig.opportunity_stages.find((p) => p.name === name)
      ?.id ?? null
  );
};

const getOpportunityFormData = () => {
  if (opportunitiesStore.selectedLeadData?.id) {
    return {
      name: `from lead: ${opportunitiesStore.selectedLeadData?.name}`,
      lead_source: opportunitiesStore.selectedLeadData?.source,
      type: "existing_business",
      description: opportunitiesStore.selectedLeadData?.description,
      stage_id: getStatusIdForOpportunityName(opportunitiesStore.createdStage),
      lead_id: opportunitiesStore.selectedLeadData?.id,
      user_id: opportunitiesStore.selectedLeadData?.agent_id,
      org_category_id: opportunitiesStore.selectedLeadData?.org_category_id,
      organization_id: opportunitiesStore.selectedLeadData?.organization_id,
      profile_id: opportunitiesStore.selectedLeadData?.profile_id,
      product_category_id:
        opportunitiesStore.selectedLeadData?.product_category_id,
      product_id: opportunitiesStore.selectedLeadData?.product_id,
      assigned_users:
        opportunitiesStore.selectedLeadData.assigned_users?.map((user) => ({
          id: user.id,
          type: user.pivot?.type || null,
        })) || [],
    };
  } else {
    return {
      stage_id: getStatusIdForOpportunityName(opportunitiesStore.createdStage),
      assigned_users: [{ id: null, type: undefined }],
    };
  }
};

const formRef = ref<ElementUIFormRef | null>(null);
const formData = reactive<Partial<Opportunity>>(getOpportunityFormData());
const size: FormSizeType = "small";

const checkProbability = (rule: any, value: any, callback: any) => {
  if (value > 100) {
    callback(new Error("Probability must not be greater than 100"));
  } else {
    callback();
  }
};

const rules = reactive<FormRuleType>({
  name: [
    {
      required: true,
      message: "Name is required",
      trigger: "blur",
    },
  ],
  assigned_users: [
    {
      required: true,
      validator: (_, value, callback) => {
        if (!value || value.length === 0) {
          callback(new Error("At least one agent must be selected"));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  lead_source: [
    {
      required: true,
      message: "Lead source is required",
      trigger: "blur",
    },
  ],
  stage_id: [
    {
      required: true,
      message: "Stage is required",
      trigger: "blur",
    },
  ],
  product_id: [
    {
      required: true,
      message: "Product is required",
      trigger: "change",
    },
  ],
  probability: [{ validator: checkProbability, trigger: "blur" }],
});

const attachmentArray = ref([] as Attachment[]);
const attachmentUrl = ref("");
const showAttachmentModal = ref(false);

const appendAttachment = (value: Attachment) => {
  attachmentArray.value.push(value);
};

const openAttachment = (url: string) => {
  attachmentUrl.value = url;
  window.open(attachmentUrl.value as unknown as string, "_blank");
};

const handleAttachmentOpen = function () {
  showAttachmentModal.value = true;
};

const closeAttachment = () => {
  showAttachmentModal.value = false;
};

const createOpportunity = () => {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
    } else if (valid) {
      formData.unique_identifiers = attachmentArray.value.map(
        (attachment) => attachment.unique_identifier,
      );
      const missingName = fileList.value.some(
        (file) => !file.attachmentName?.trim(),
      );
      if (missingName) {
        ElNotification({
          title: "Name is required",
          message: "Please provide a name for each attachment.",
          type: "error",
        });
        return;
      }
      const attachments = fileList.value.map((file) => ({
        file: file.raw,
        name: file.attachmentName,
        description: file.description || "",
      }));
      const opportunity = await opportunitiesStore.createOpportunity(
        formData,
        attachments,
      );
      if (opportunity) {
        ElNotification({
          title: "Opportunity created",
          message: "New opportunity created",
          type: "success",
        });
      }
      opportunitiesStore.showKanbanCreateDialog = false;
      opportunitiesStore.view === OpportunityView.TABLE
        ? opportunitiesStore.getOpportunities()
        : opportunitiesStore.getStageWiseOpportunities();
      opportunitiesStore.closeOpportunityCreateModal();
      if (opportunitiesStore.selectedLeadData.id) {
        await leadsStore.getLead(opportunitiesStore.selectedLeadData.id);
      }
    } else {
      console.log("error in validation");
    }
  });
};

const closeModal = () => {
  ElNotification({
    type: "warning",
    title: "Cancelled",
  });

  opportunitiesStore.showKanbanCreateDialog = false;
  opportunitiesStore.closeOpportunityCreateModal();
};

const onChangeOrgCategory = async () => {
  if (formData.org_category_id) {
    formData.organization_id = null;
    organizationStore.setSimpleFilter({
      category_id: formData.org_category_id,
    });
    await organizationStore.getSimpleOrganizations();
  }
};
const onChangeProductCategory = async () => {
  if (formData.product_category_id) {
    formData.product_id = null;
    productStore.setSimpleFilter({
      category_id: formData.product_category_id,
    });
    await productStore.getAllProducts();
  }
};

const onChangeOrganization = async () => {
  formData.profile_id = null;

  if (formData.organization_id) {
    profileStore.setSimpleFilter({
      organization_id: formData.organization_id,
    });
  } else {
    profileStore.setSimpleFilter({});
  }
  await profileStore.getSimpleProfiles();
};

const onChangeProfile = async () => {
  if (formData.organization_id) {
    profileStore.setSimpleFilter({
      organization_id: formData.organization_id,
    });
  } else {
    profileStore.setSimpleFilter({});
  }
  await profileStore.getSimpleProfiles();
};

const { remoteMethod, loading } = useDebouncedSearch((query) => {
  profileStore._simpleFilter.search = query;
  profileStore.getSimpleProfiles();
});
const handleUserSelection = () => {
  formData.assigned_users = formData.assigned_users?.map((user) => ({
    id: user.id,
    type: user.type || undefined,
  }));
};
const assignUser = () => {
  formData.assigned_users?.push({ id: null, type: undefined });
};
const removeUser = (index: number) => {
  formData.assigned_users?.splice(index, 1);
};
const handleFileChange: UploadProps["onChange"] = (uploadFile) => {
  const exists = fileList.value.find((file) => file.uid === uploadFile.uid);

  if (!uploadFile.url && uploadFile.raw?.type.startsWith("image")) {
    uploadFile.url = URL.createObjectURL(uploadFile.raw);
  }
  if (!uploadFile.attachmentName) {
    uploadFile.attachmentName = uploadFile.name
      .replace(/\.[^/.]+$/, "")
      .split(" ")[0];
  }

  if (!exists) {
    fileList.value.push({
      ...uploadFile,
      attachmentName: uploadFile.name,
      description: "",
    });
  }
};
const removeFile = (index: number) => {
  const file = fileList.value[index];
  if (file.url) {
    URL.revokeObjectURL(file.url);
  } else if (file.raw) {
    URL.revokeObjectURL(URL.createObjectURL(file.raw));
  }
  fileList.value.splice(index, 1);
};
const getAcceptValue = () => {
  const accept =
    ".jpg, .jpeg, .png, .pdf,.ppt, .pptx, .xlx, .xlxs, .doc, .docx, .wav, .heic, .mp3, .mp4";
  return accept;
};
const handlePreview: UploadProps["onPreview"] = (file) => {
  const exists = fileList.value.find((f) => f.uid === file.uid);
  if (!exists) {
    if (!file.url && file.raw?.type.startsWith("image")) {
      file.url = URL.createObjectURL(file.raw);
    }
    fileList.value.push(file);
  }
};
</script>
<template>
  <EditModalHeader v-if="props.showHeader">
    <template #title> Create Opportunity</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData as Opportunity"
    class="space-y-5"
    :rules="rules"
    @submit.prevent="createOpportunity"
  >
    <div class="feature-filter-grid-1">
      <el-form-item class="form-elem-container" prop="name">
        <div class="form-elem-container">
          <FormLabel :required="true">Name</FormLabel>
          <el-input
            v-model="formData.name"
            clearable
            placeholder="Please input name"
          />
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="country">
        <div class="form-elem-container">
          <FormLabel>Country</FormLabel>

          <el-select
            v-model="formData.country"
            class="w-full"
            clearable
            filterable
            placeholder="Please select country"
          >
            <el-option
              v-for="country in configStore.getCountries()"
              :key="country?.name"
              :label="country?.name.common"
              :value="country?.name.common"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="assigned_users">
        <div class="form-elem-container">
          <div class="flex items-center justify-between gap-2">
            <FormLabel :required="true">Assigned Agent</FormLabel>
            <UsersAddButton />
          </div>
          <div v-for="(_, index) in formData.assigned_users" :key="index">
            <div class="flex flex-col sm:flex-row items-center">
              <el-select
                v-model="formData.assigned_users[index].id"
                placeholder="Select User"
                filterable
                style="width: 100%"
                @change="handleUserSelection"
              >
                <el-option
                  v-for="agent in configStore.globalConfig.subordinates"
                  v-if="configStore.isOrgStructureHierarchical"
                  :key="agent.id"
                  :label="agent.name"
                  :value="agent.id"
                />
                <el-option
                  v-for="agent in configStore.globalConfig.agents"
                  v-if="!configStore.isOrgStructureHierarchical"
                  :key="agent.id"
                  :label="agent.name"
                  :value="agent.id"
                />
              </el-select>
              <el-select
                v-model="formData.assigned_users[index].type"
                placeholder="Select Type"
                clearable
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="type in configStore.globalConfig.crm
                    .opportunity_users_association_types"
                  :key="type"
                  :label="type"
                  :value="type"
                />
              </el-select>
              <el-icon class="ml-2 cursor-pointer" @click="removeUser(index)">
                <Close
              /></el-icon>
            </div>
          </div>
          <button
            class="cursor-pointer text-white bg-light-purple text-xs w-fit px-2 py-2 mt-2 rounded"
            :disabled="
              !isUserPermitted(PermissionNames.OPPORTUNITY_ASSIGN_USER)
            "
            :title="
              !isUserPermitted(PermissionNames.OPPORTUNITY_ASSIGN_USER)
                ? 'You don\'t have permission'
                : ''
            "
            @click.prevent="assignUser"
          >
            <span> Assign User </span>
          </button>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="organization">
        <div class="form-elem-container">
          <FormLabel>Organization Category</FormLabel>
          <el-select
            v-model="formData.org_category_id"
            filterable
            clearable
            class="w-anizationfull"
            placeholder="Please select organization category"
            @change="onChangeOrgCategory"
          >
            <el-option
              v-for="category in configStore.globalConfig
                .organization_categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="organization_id">
        <div class="form-elem-container">
          <FormLabel>Organization</FormLabel>
          <div class="flex items-center gap-2">
            <el-select
              v-model="formData.organization_id"
              filterable
              clearable
              class="w-full"
              placeholder="Please select organization"
              @change="onChangeOrganization"
            >
              <el-option
                v-for="org in organizationStore.simpleOrganizations"
                :key="org.id"
                :label="org.name"
                :value="org.id"
              ></el-option>
            </el-select>
            <OrganizationsAddButton />
          </div>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="profile_id">
        <div class="form-elem-container">
          <FormLabel>Profile</FormLabel>
          <div class="flex items-center gap-2">
            <el-select
              v-model="formData.profile_id"
              filterable
              clearable
              class="w-full"
              placeholder="Search by name, phone or email"
              remote
              reserve-keyword
              :remote-method="remoteMethod"
              :loading="loading"
              @clear="onChangeProfile"
            >
              <el-option
                v-for="profile in profileStore.simpleProfiles"
                :key="profile.id"
                :label="
                  profile.default_phone
                    ? `${profile.name} | ${profile.default_phone.phone}`
                    : profile.name
                "
                :value="profile.id"
              ></el-option>
            </el-select>
            <ProfileAddButton
              :org-id="formData.organization_id"
              :is-from-opp="true"
            />
          </div>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="type">
        <div class="form-elem-container">
          <FormLabel>Type</FormLabel>
          <el-select
            v-model="formData.type"
            class="w-full"
            clearable
            placeholder="Please select type"
          >
            <el-option
              v-for="type in Object.values(OpportunityType)"
              :key="type"
              :label="snakeToTitleCase(type)"
              :value="type"
            />
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="stage_id" clearable>
        <div class="form-elem-container">
          <FormLabel :required="true">Stage</FormLabel>
          <el-select
            v-model="formData.stage_id"
            class="w-full"
            clearable
            placeholder="Please select stage"
          >
            <el-option
              v-for="stage in configStore.globalConfig.opportunity_stages"
              :key="stage.id"
              :label="stage.name"
              :value="stage.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="lead_source">
        <div class="form-elem-container">
          <FormLabel :required="true">Lead Source</FormLabel>
          <el-select
            v-model="formData.lead_source"
            class="w-full"
            clearable
            placeholder="Please select lead source"
          >
            <el-option
              v-for="source in configStore.globalConfig.crm.lead_source_list"
              :key="source"
              :label="snakeToTitleCase(source)"
              :value="source"
            />
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="organization">
        <div class="form-elem-container">
          <FormLabel>Product Category</FormLabel>
          <el-select
            v-model="formData.product_category_id"
            filterable
            clearable
            class="w-full"
            placeholder="Please select product category"
            @change="onChangeProductCategory"
          >
            <el-option
              v-for="category in configStore.globalConfig.product_categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="product_id">
        <div class="form-elem-container">
          <FormLabel :required="true">Product</FormLabel>
          <div class="flex items-center gap-2">
            <el-select
              v-model="formData.product_id"
              filterable
              class="w-full"
              clearable
              placeholder="Please select a product"
            >
              <el-option
                v-for="product in productStore.allProducts"
                :key="product.id"
                :label="product.name"
                :value="product.id"
              ></el-option>
            </el-select>
            <ProductsAddButton
              :product-category-id="formData.product_category_id"
            />
          </div>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="amount">
        <div class="form-elem-container">
          <FormLabel>Amount</FormLabel>
          <el-input
            v-model="formData.amount"
            clearable
            type="number"
            placeholder="Please input  amount"
          />
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="probability">
        <div class="form-elem-container">
          <FormLabel>Probability</FormLabel>
          <el-input
            v-model="formData.probability"
            type="number"
            clearable
            placeholder="Please input probability"
          />
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="close_date">
        <div class="form-elem-container">
          <FormLabel>Close Date</FormLabel>
          <el-date-picker
            v-model="formData.close_date"
            type="date"
            value-format="YYYY-MM-DD"
            class="datePickerClass"
            placeholder="Please pick a close date"
          />
        </div>
      </el-form-item>
      <el-form-item
        class="form-elem-container"
        prop="closing_business_value_pri"
      >
        <div class="form-elem-container">
          <FormLabel
            >Closing Business Value ({{
              configStore.globalConfig.crm.primary_currency?.view
            }})</FormLabel
          >
          <el-input
            v-model="formData.closing_business_value_pri"
            type="number"
            class="w-full"
          />
        </div>
      </el-form-item>
      <el-form-item
        v-if="configStore.globalConfig.crm.secondary_currency"
        class="form-elem-container"
        prop="closing_business_value_sec"
      >
        <div class="form-elem-container">
          <FormLabel
            >Closing Business Value ({{
              configStore.globalConfig.crm.secondary_currency?.view
            }})</FormLabel
          >
          <el-input
            v-model="formData.closing_business_value_sec"
            type="number"
            class="w-full"
          />
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="description">
        <div class="form-elem-container">
          <FormLabel> Description</FormLabel>
          <el-input
            v-model="formData.description"
            :rows="2"
            :size="size"
            type="textarea"
            placeholder="Please input description"
          />
        </div>
      </el-form-item>

      <el-form-item prop="attachments">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col md:flex-row md:items-center gap-2">
            <FormLabel>Attachments</FormLabel>
            <div class="ml-2 mb-2">
              <el-button
                size="small"
                plain
                @click="$refs.uploadRef?.$el.querySelector('input').click()"
              >
                <DocumentAdd class="w-4 h-4" />
                <p>Add Attachments</p>
              </el-button>
            </div>
          </div>

          <el-upload
            ref="uploadRef"
            v-model:file-list="fileList"
            :auto-upload="false"
            :multiple="true"
            :show-file-list="false"
            :on-preview="handlePreview"
            :on-change="handleFileChange"
            :accept="getAcceptValue()"
            style="display: none"
          />

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              v-for="(file, index) in fileList"
              :key="file.uid"
              class="flex items-center gap-4 border p-2 rounded-md"
            >
              <div
                class="w-16 h-16 flex items-center justify-center bg-gray-100 rounded"
              >
                <img
                  v-if="file.url || file.raw?.type.startsWith('image')"
                  :src="file.url"
                  class="w-full h-full object-cover rounded cursor-pointer"
                  @click="
                    () => {
                      previewIndex = index;
                      showViewer = true;
                    }
                  "
                />
                <el-icon v-else-if="file.raw?.type?.startsWith('audio')"
                  ><Headset
                /></el-icon>
                <el-icon v-else-if="file.raw?.type?.startsWith('video')"
                  ><VideoCamera
                /></el-icon>
                <el-icon v-else-if="file.raw?.type === 'application/pdf'"
                  ><Document
                /></el-icon>
                <el-icon v-else><Folder /></el-icon>
              </div>

              <div class="flex flex-col gap-1">
                <el-input
                  v-model="file.attachmentName"
                  placeholder="File name"
                  class="w-1/3"
                />
                <el-input
                  v-model="file.description"
                  placeholder="File description"
                  type="textarea"
                />
              </div>

              <el-button circle size="small" @click="removeFile(index)">
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="feature-filter-submit">
      <NavButton @click="closeModal"> Cancel</NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.OPPORTUNITY_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>

  <CrmAttachmentForm
    :show-header="false"
    :show-attachment-modal="showAttachmentModal"
    :related-type="'opportunity'"
    @add-attachment="appendAttachment"
    @close-attachment-modal="closeAttachment"
  />
</template>

<style setup>
.datePickerClass {
  width: 100% !important;
}
</style>
